<script setup lang="ts">
const props = defineProps({
  showMenu: { type: Boolean, required: true },
  user: {
    type: Object as PropType<ReturnType<typeof useAuth>['data']['value']>,
    default: null,
    required: false
  },
  role: { type: String, default: 'candidate' }
})

defineEmits(['handleLogout', 'clickMenu'])

const showAvatarMenu = ref(false)

const imageUrl = computed(() => props.user?.profile_image?.presigned_get_object_url || '')
const firstName = computed(() => props.user?.first_name || '')
const lastName = computed(() => props.user?.last_name || '')
const name = computed(() => (firstName.value ? `${firstName.value} ${lastName.value}` : ''))
</script>

<template>
  <NuxtLink class="menu-btn clickable" to="/notifications">
    <CommonIconNotificationsBell id="bell" />
  </NuxtLink>

  <div id="avatar-btn" class="menu-btn clickable" @click="showAvatarMenu = !showAvatarMenu">
    <div id="avatar-btn-inner">
      <div id="avatar-btn-avatar">
        <PageForumAvatarInitials :first-name="firstName" :last-name="lastName" :image="imageUrl" />
      </div>

      <CommonIconChevron />
    </div>

    <CommonMenuDropdown :show-menu="showAvatarMenu" @close-menu="showAvatarMenu = false">
      <div id="navbar-dropdown-name">
        <span class="max-one-line">{{ name }}</span>
      </div>

      <div v-if="role === 'candidate'" @click="navigateTo('/profile')">
        <CommonIconSidebarProfile />
        <span>Profile</span>
      </div>

      <div @click="navigateTo('/settings')">
        <CommonIconSidebarSettings />
        <span>Settings</span>
      </div>

      <div id="navbar-dropdown-logout" @click="$emit('handleLogout')">
        <CommonIconNavbarLogout />
        <span>Log out</span>
      </div>
    </CommonMenuDropdown>
  </div>

  <div
    id="navbar-menu-btn"
    :class="`menu-btn clickable ${showMenu ? 'close-btn' : ''}`"
    @click="$emit('clickMenu')"
  >
    <CommonIconMenu v-if="!showMenu" id="menu" class="anim-fade" />
    <CommonIconClose v-if="showMenu" id="close" class="anim-fade" />
  </div>
</template>

<style scoped lang="scss">
.menu-btn {
  $menuSize: 37px;
  height: $menuSize;
  width: $menuSize;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  position: relative;
  transition: background 0.3s ease-out;

  svg {
    fill: var(--textM2);
  }

  #bell {
    $bellSize: 23px;
    height: $bellSize;
    width: $bellSize;
  }

  #menu {
    $menuSvgSize: 16px;
    height: $menuSvgSize;
    width: $menuSvgSize;
  }

  #close {
    $closeSize: 16px;
    height: $closeSize;
    fill: var(--purple);
    width: $closeSize;
  }
}

.close-btn {
  background: var(--lightPurple) !important;
}

#avatar-btn {
  $avatarSize: 40px;
  height: $avatarSize;
  width: auto;

  &-inner {
    display: flex;
    gap: 9px;
    align-items: center;
    height: 100%;

    svg {
      height: 11px;
      width: 11px;
      transform: rotate(90deg);
      fill: var(--purple);
    }
  }

  &-avatar {
    height: 100%;
    padding: 2px;
    border: 1px solid var(--purple);
    width: $avatarSize;
    border-radius: 50%;
  }
}

#navbar-menu-btn {
  display: none;
}

#navbar-dropdown {
  &-name {
    cursor: default;
    border-bottom: 1px solid var(--borderL2);
    padding-bottom: 20px;
    margin-bottom: 10px;

    span {
      display: block;
      max-width: 170px;
      font-size: 0.77rem;
      color: var(--textM2);
      font-weight: 500;
      text-transform: uppercase;
    }
  }

  &-logout {
    border-top: 1px solid var(--borderL2);
    padding-top: 20px;
    margin-top: 10px;
  }
}

@media (max-width: 900px) {
  .menu-btn {
    background-color: var(--bgMedium);
    height: 40px;
    width: 40px;
  }

  #avatar-btn {
    display: none;
  }

  #navbar-menu-btn {
    display: flex;
  }
}

@media (hover: hover) {
  #navbar-dropdown {
    &-name:hover {
      background: white;
    }
  }
}
</style>
